import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { TEAM_ACCEPT_RETURN_TO } from './teamAcceptReturnTo';
import { TypedMemo } from '../../common/types';
import { useQueryParam } from '../../../utils/hooks/useQueryParam';
import { useAuthFormPasswordless } from './useAuthFormPasswordless';
import { useInviteWorkspaceName } from './useInviteWorkspaceName';
import { AuthTopBar } from './components/AuthTopBar';
import { AuthEmailStep, AuthInviteInfo } from './components/AuthEmailStep';
import { AuthMode } from './components/AuthEmailHeading';
import { AuthCodeStep } from './components/AuthCodeStep';
import { AuthCampaignVisual } from './components/AuthCampaignVisual';
import { TurnstileSlot } from './turnstile/TurnstileSlot';
import { AuthPasswordSignIn, AuthPasswordSignInButton } from './components/AuthPasswordSignIn';
import { authInitialMode } from './authInitialMode';
import { isOAuthConsentReturnTo } from '../../../routing/sml/signInReturnTo';
import './SMLAuthViewRenderer.scss';

/** The return path `TeamAcceptView` sends a signed-out invitee with (design: onboarding g7). */

/**
 * `/app/auth`: the first screen after "Start free" on music24.com.
 * design: onboarding g1-g10 (docs/superpowers/design-reference/music24-onboarding/g Sign Up and Sign In.dc.html).
 * The flow is the same as before: `useAuthFormPasswordless` and `sso/*` do the work.
 */
const SMLAuthViewRendererComponent: React.FC = () => {
  const form = useAuthFormPasswordless();
  const modeParam = useQueryParam('mode', null, '');
  const returnTo = useQueryParam('returnTo', null, '');
  const sourceType = useQueryParam('sourceType', null, '');
  const linkMode = useMemo(
    () => authInitialMode({ mode: modeParam, returnTo, sourceType }),
    [modeParam, returnTo, sourceType]
  );
  const [mode, setMode] = useState<AuthMode>(linkMode);
  // A new link on the same page (a client-side navigation) sets the copy of that link again.
  useEffect(() => {
    setMode(linkMode);
  }, [linkMode]);

  // The invite headline never prints names from the URL: anyone can write a link that says "Join
  // <any name>" on music24.com. The API reads the workspace name from the invite token (Ruling F2).
  // The API gives no inviter name (it would be an email), so the lead has none.
  const isInvite = returnTo === TEAM_ACCEPT_RETURN_TO;
  const workspaceName = useInviteWorkspaceName(isInvite);
  const invite = useMemo<AuthInviteInfo | null>(
    () => (isInvite ? { workspaceName, inviterName: null } : null),
    [isInvite, workspaceName]
  );

  const isEmailStep = form.step === 'email';
  const isOAuthFlow = isOAuthConsentReturnTo(returnTo);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const openPassword = useCallback(() => setPasswordOpen(true), []);
  const closePassword = useCallback(() => setPasswordOpen(false), []);
  // The hidden Turnstile check sits in the form of each email-code step (design: owner request
  // 2026-10-09). It takes no room unless Cloudflare asks for an interactive check.
  const securityCheck = (
    <TurnstileSlot
      slotRef={form.turnstile.slotRef}
      isVisible={form.turnstile.isVisible}
      needsInteraction={form.turnstile.needsInteraction}
    />
  );

  return (
    <div className="sml-auth">
      <AuthTopBar mode={mode} onSwitchMode={isEmailStep && invite === null ? setMode : null} />
      <main className="au-grid">
        {isEmailStep && passwordOpen ? (
          <div className="au-step">
            <AuthPasswordSignIn onBack={closePassword} />
          </div>
        ) : null}
        {isEmailStep && !passwordOpen ? (
          <AuthEmailStep
            email={form.emailInput}
            setEmail={form.setEmailInput}
            onSubmit={form.submitEmail}
            onHaveCode={form.openCodeStep}
            isLoading={form.isLoading}
            error={form.error}
            mode={mode}
            invite={invite}
            securityCheck={securityCheck}
            securityCheckPending={form.turnstile.needsInteraction}
            // A directory reviewer signs in with a password (spec 2026-10-10 MCP apps §3.3): only on
            // the OAuth flow, never on the normal sign-in page.
            afterSso={isOAuthFlow ? <AuthPasswordSignInButton onClick={openPassword} /> : null}
          />
        ) : null}
        {!isEmailStep ? (
          <AuthCodeStep
            email={form.email}
            code={form.code}
            setCode={form.setCode}
            onVerify={form.verify}
            isRefusedCode={form.isRefusedCode}
            codeSentAt={form.codeSentAt}
            onResend={form.resend}
            onBack={form.goBackToEmail}
            isLoading={form.isLoading}
            isResending={form.isResending}
            error={form.error}
            securityCheck={securityCheck}
            securityCheckPending={form.turnstile.needsInteraction}
          />
        ) : null}
        <AuthCampaignVisual />
      </main>
    </div>
  );
};

export const SMLAuthViewRenderer = TypedMemo(SMLAuthViewRendererComponent);
